// Entry point for the inline product customiser bundle.
//
// Built by esbuild to worker/public/customiser.js. Loaded on demand by
// embed.js when a `<rk-product-customiser>` custom element mounts on the
// PDP. Exposes a single global, `window.__RK_PC_MOUNT__(host, ctx)`,
// which the launcher invokes to mount the React tree into the element.
//
// Styles are imported as text (esbuild `loader: { ".css": "text" }`) and
// injected via a singleton <style> in document.head so the design works
// regardless of where on the theme the element lives.

import { createRoot, type Root } from "react-dom/client";
import { App, type MountContext } from "./App";
// eslint-disable-next-line import/no-unresolved
import styles from "./styles.css";

const STYLE_ID = "rk-pc-styles";

function ensureStyles() {
  if (typeof document === "undefined") return;
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement("style");
  s.id = STYLE_ID;
  s.textContent = styles as unknown as string;
  document.head.appendChild(s);
}

const ROOT_KEY = "__RK_PC_ROOT__";

declare global {
  interface Window {
    __RK_PC_MOUNT__?: (host: HTMLElement, ctx: MountContext) => void;
  }
}

window.__RK_PC_MOUNT__ = (host: HTMLElement, ctx: MountContext) => {
  ensureStyles();
  // Prevent double-mount if the launcher fires twice (some themes re-run
  // section JS on variant change).
  const anyHost = host as HTMLElement & { [ROOT_KEY]?: Root };
  if (anyHost[ROOT_KEY]) {
    anyHost[ROOT_KEY]!.render(<App ctx={ctx} />);
    return;
  }
  // Phase 68: the Liquid embed ships the host with a lightweight
  // "Preparing your design tools..." skeleton so the slot is never
  // blank while the bundle downloads. Clear it before createRoot, which
  // otherwise warns about adopting non-empty containers.
  host.replaceChildren();
  const root = createRoot(host);
  anyHost[ROOT_KEY] = root;
  root.render(<App ctx={ctx} />);
};
